// @givmo/app-admin — employer BULK gift-card RECIPIENT multi-select (EP-WEB5).
//
// Two ways to build ONE recipient set for a batch, both feeding the same controlled `selected` list:
//   1. ROSTER multi-select — a searchable checkbox list over `GET /admin/company/employees` (the
//      single-picker's `useInfiniteQuery` roster), with select-all-shown. An employee with NO work
//      email on file is shown but DISABLED (visible-but-disabled beats silent omission — the admin sees
//      who they can't gift, and why), exactly like the single recipient picker.
//   2. CSV / TEXT PASTE — a textarea of work emails (one-per-line or comma/semicolon/space-separated),
//      parsed + deduped client-side; email-shaped tokens merge into the selection, junk tokens are
//      echoed back as `invalid` for the admin to fix. The server's per-entry 422 remains the truth on
//      roster membership (a pasted address that isn't on the roster 422s the whole batch, surfaced in
//      the confirm) — this local pass only removes obvious non-emails and duplicates.
//
// Recipients are addressed by `work_email`; the backend matches it EXACTLY (case-sensitive), so pasted
// tokens keep their original case and are deduped case-insensitively (one person ⇒ one card).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyEmployeeOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  Button,
  Checkbox,
  EmptyState,
  Icon,
  SearchInput,
  Skeleton,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import { Users, X as XIcon } from 'lucide-react';
import { employeeFullName } from '../employees/employees-decode';
import {
  mergeRecipients,
  parseRecipientPaste,
  recipientDedupeKey,
  type BatchRecipient,
} from './gift-cards-batch-decode';

const PREFIX = 'gift-card-bulk-recipient';
const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

const LIST_KEY = (q: string) => ['companies-get_company_employees', 'gift-cards-bulk', q] as const;

async function fetchEmployeesPage(
  q: string,
  offset: number,
  signal: AbortSignal,
): Promise<CompanyEmployeeOut[]> {
  const trimmed = q.trim();
  const { data } = await sdk.companiesGetCompanyEmployees({
    query: {
      ...(trimmed.length > 0 ? { q: trimmed } : {}),
      'page[limit]': PAGE_LIMIT,
      'page[offset]': offset,
    },
    signal,
    throwOnError: true,
  });
  return extractList<CompanyEmployeeOut>(data);
}

/** The work email an employee is gifted at, or null when none is on file (→ not selectable). */
function recipientWorkEmail(employee: CompanyEmployeeOut): string | null {
  const email = employee.work_email;
  return typeof email === 'string' && email.trim().length > 0 ? email.trim() : null;
}

type PasteFeedback = { added: number; skipped: number; invalid: string[] } | null;

export interface BulkRecipientPickerProps {
  selected: BatchRecipient[];
  onChange: (next: BatchRecipient[]) => void;
  /** The per-batch count cap (D-EP-16) — an over-cap selection is surfaced here as a warning. */
  maxCount: number;
}

export function BulkRecipientPicker({ selected, onChange, maxCount }: BulkRecipientPickerProps) {
  const [mode, setMode] = useState<'roster' | 'paste'>('roster');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [pasteRaw, setPasteRaw] = useState('');
  const [pasteFeedback, setPasteFeedback] = useState<PasteFeedback>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const list = useInfiniteQuery({
    queryKey: LIST_KEY(query),
    queryFn: ({ pageParam, signal }: QueryFunctionContext<ReturnType<typeof LIST_KEY>, number>) =>
      fetchEmployeesPage(query, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
    enabled: mode === 'roster',
  });

  const employees = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  const selectedKeys = useMemo(
    () => new Set(selected.map((r) => recipientDedupeKey(r.workEmail))),
    [selected],
  );

  const toggleOne = (recipient: BatchRecipient, checked: boolean) => {
    const key = recipientDedupeKey(recipient.workEmail);
    if (checked) {
      if (selectedKeys.has(key)) return;
      onChange([...selected, recipient]);
    } else {
      onChange(selected.filter((r) => recipientDedupeKey(r.workEmail) !== key));
    }
  };

  // The selectable employees currently loaded (those with a work email).
  const selectablePage = useMemo(() => {
    const out: BatchRecipient[] = [];
    for (const e of employees) {
      const workEmail = recipientWorkEmail(e);
      if (workEmail !== null) out.push({ workEmail, name: employeeFullName(e) || 'Employee' });
    }
    return out;
  }, [employees]);

  const allShownSelected =
    selectablePage.length > 0 &&
    selectablePage.every((r) => selectedKeys.has(recipientDedupeKey(r.workEmail)));

  const toggleAllShown = (checked: boolean) => {
    if (checked) {
      onChange(mergeRecipients(selected, selectablePage).recipients);
    } else {
      const pageKeys = new Set(selectablePage.map((r) => recipientDedupeKey(r.workEmail)));
      onChange(selected.filter((r) => !pageKeys.has(recipientDedupeKey(r.workEmail))));
    }
  };

  const addPaste = () => {
    const parsed = parseRecipientPaste(pasteRaw);
    const { recipients, added, skipped } = mergeRecipients(
      selected,
      parsed.emails.map((workEmail) => ({ workEmail })),
    );
    onChange(recipients);
    setPasteFeedback({ added, skipped: skipped + parsed.duplicates, invalid: parsed.invalid });
    setPasteRaw('');
  };

  const removeOne = (workEmail: string) => {
    const key = recipientDedupeKey(workEmail);
    onChange(selected.filter((r) => recipientDedupeKey(r.workEmail) !== key));
  };

  const overCap = selected.length > maxCount;

  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}`)}>
      {/* ── Selected summary (count chip + removable chips + clear) ── */}
      <SelectedSummary
        selected={selected}
        onRemove={removeOne}
        onClear={() => onChange([])}
        overCap={overCap}
        maxCount={maxCount}
      />

      {/* ── Mode toggle: roster ↔ paste ── */}
      <div className="flex gap-8" {...testIdProp(`${PREFIX}-mode`)}>
        <Button
          appearance="saas"
          tone={mode === 'roster' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setMode('roster')}
          testId={`${PREFIX}-mode-roster`}
        >
          Choose from roster
        </Button>
        <Button
          appearance="saas"
          tone={mode === 'paste' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setMode('paste')}
          testId={`${PREFIX}-mode-paste`}
        >
          Paste emails
        </Button>
      </div>

      {mode === 'roster' ? (
        <div className="flex flex-col gap-12">
          <SearchInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput('')}
            placeholder="Search employees by name or email…"
            aria-label="Search recipients"
            testId={`${PREFIX}-search`}
          />

          {selectablePage.length > 0 && (
            <label
              className="flex items-center gap-10 rounded-lg border border-border-subtle px-12 py-8"
              {...testIdProp(`${PREFIX}-select-all`)}
            >
              <Checkbox
                appearance="saas"
                checked={allShownSelected}
                onCheckedChange={toggleAllShown}
                testId={`${PREFIX}-select-all-box`}
              />
              <span className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
                Select all shown
              </span>
            </label>
          )}

          <RosterResults
            loading={list.isPending}
            error={list.isError}
            errorMessage={
              isGivmoError(list.error) ? list.error.message : 'Couldn’t load your employees.'
            }
            employees={employees}
            selectedKeys={selectedKeys}
            onToggle={toggleOne}
            onRetry={() => void list.refetch()}
            hasMore={list.hasNextPage === true}
            loadingMore={list.isFetchingNextPage}
            onLoadMore={() => void list.fetchNextPage()}
            searching={query.trim().length > 0}
          />
        </div>
      ) : (
        <PastePanel
          value={pasteRaw}
          onChange={(v) => setPasteRaw(v)}
          onAdd={addPaste}
          feedback={pasteFeedback}
        />
      )}
    </div>
  );
}

// ── Selected summary ────────────────────────────────────────────────────────────────────────────

function SelectedSummary({
  selected,
  onRemove,
  onClear,
  overCap,
  maxCount,
}: {
  selected: BatchRecipient[];
  onRemove: (workEmail: string) => void;
  onClear: () => void;
  overCap: boolean;
  maxCount: number;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <span
          className="font-sans text-s13 font-w600 tracking-givmo text-text-strong tabular-nums"
          {...testIdProp(`${PREFIX}-count`)}
        >
          {selected.length} recipient{selected.length === 1 ? '' : 's'} selected
        </span>
        {selected.length > 0 && (
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            onClick={onClear}
            testId={`${PREFIX}-clear-all`}
          >
            Clear all
          </Button>
        )}
      </div>

      {overCap && (
        <div
          className="flex items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-12"
          {...testIdProp(`${PREFIX}-over-cap`)}
        >
          <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
            A batch can have at most {maxCount} recipients. Remove {selected.length - maxCount} to
            continue.
          </p>
        </div>
      )}

      {selected.length > 0 && (
        <ul
          className="flex max-h-[160px] flex-wrap gap-8 overflow-y-auto"
          {...testIdProp(`${PREFIX}-chips`)}
        >
          {selected.map((r) => (
            <li key={recipientDedupeKey(r.workEmail)}>
              <span
                className="flex items-center gap-6 rounded-full border border-border-subtle bg-surface-sunken py-4 pl-10 pr-6 font-sans text-s12 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-chip-${recipientDedupeKey(r.workEmail)}`)}
              >
                <span className="truncate">{r.name?.trim() || r.workEmail}</span>
                <button
                  type="button"
                  onClick={() => onRemove(r.workEmail)}
                  aria-label={`Remove ${r.workEmail}`}
                  className="flex size-16 items-center justify-center rounded-full text-text-tertiary outline-none hover:bg-surface-hover hover:text-text-strong focus-visible:shadow-ring"
                  {...testIdProp(`${PREFIX}-chip-remove-${recipientDedupeKey(r.workEmail)}`)}
                >
                  <Icon icon={XIcon} size={12} aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Paste panel ─────────────────────────────────────────────────────────────────────────────────

function PastePanel({
  value,
  onChange,
  onAdd,
  feedback,
}: {
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  feedback: PasteFeedback;
}) {
  return (
    <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-paste`)}>
      <Textarea
        label="Paste work emails"
        placeholder={'one per line, or comma-separated\nada@corp.test, grace@corp.test'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[120px]"
        testId={`${PREFIX}-paste-input`}
      />
      <div className="flex items-center justify-end">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          disabled={value.trim().length === 0}
          onClick={onAdd}
          testId={`${PREFIX}-paste-add`}
        >
          Add emails
        </Button>
      </div>

      {feedback !== null && (
        <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-paste-feedback`)}>
          {(feedback.added > 0 || feedback.skipped > 0) && (
            <p className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
              Added {feedback.added}
              {feedback.skipped > 0 ? `, skipped ${feedback.skipped} duplicate(s)` : ''}.
            </p>
          )}
          {feedback.invalid.length > 0 && (
            <div
              className="flex flex-col gap-4 rounded-lg border border-danger-100 bg-danger-50 p-12"
              {...testIdProp(`${PREFIX}-paste-invalid`)}
            >
              <p className="font-sans text-s13 font-w600 tracking-givmo text-danger-700">
                {feedback.invalid.length} entr{feedback.invalid.length === 1 ? 'y was' : 'ies were'}{' '}
                not a valid email:
              </p>
              <p className="break-words font-sans text-s12 tracking-givmo text-danger-700">
                {feedback.invalid.join(', ')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Roster results (checkbox list) ────────────────────────────────────────────────────────────────

function RosterResults({
  loading,
  error,
  errorMessage,
  employees,
  selectedKeys,
  onToggle,
  onRetry,
  hasMore,
  loadingMore,
  onLoadMore,
  searching,
}: {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  employees: CompanyEmployeeOut[];
  selectedKeys: Set<string>;
  onToggle: (recipient: BatchRecipient, checked: boolean) => void;
  onRetry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  searching: boolean;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore) onLoadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  if (loading) {
    return (
      <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-results`)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-10 rounded-lg border border-border-subtle p-12"
          >
            <Skeleton className="size-20 shrink-0 rounded-sm" />
            <Skeleton className="h-12 flex-1" />
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-8 py-16 text-center">
        <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (employees.length === 0) {
    return (
      <EmptyState
        testId={`${PREFIX}-empty`}
        icon={<Icon icon={Users} size={20} aria-hidden />}
        title="No employees found"
        description={
          searching
            ? 'No employees match your search. Try a different name or email.'
            : 'Employees who join your company program will appear here.'
        }
      />
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-results`)}>
        {employees.map((employee) => {
          const name = employeeFullName(employee) || 'Employee';
          const workEmail = recipientWorkEmail(employee);
          const selectable = workEmail !== null;
          const checked = selectable && selectedKeys.has(recipientDedupeKey(workEmail));
          return (
            <li key={employee.id}>
              <label
                className={`flex w-full items-center gap-10 rounded-lg border p-12 text-left transition-colors ${
                  selectable
                    ? 'cursor-pointer border-border-subtle hover:bg-surface-hover'
                    : 'cursor-not-allowed border-border-subtle opacity-60'
                } ${checked ? 'border-brand-500 bg-surface-sunken' : ''}`}
                {...testIdProp(`${PREFIX}-result-${employee.id}`)}
              >
                <Checkbox
                  appearance="saas"
                  checked={checked}
                  disabled={!selectable}
                  onCheckedChange={(next) => {
                    if (workEmail !== null) onToggle({ workEmail, name }, next);
                  }}
                  testId={`${PREFIX}-check-${employee.id}`}
                />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-w600 text-text-strong">{name}</span>
                  {selectable ? (
                    <span className="truncate text-s12 text-text-tertiary">{workEmail}</span>
                  ) : (
                    <span
                      className="truncate text-s12 text-text-tertiary"
                      {...testIdProp(`${PREFIX}-no-email-${employee.id}`)}
                    >
                      No work email on file
                    </span>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="flex items-center justify-center">
          <div ref={sentinelRef} aria-hidden className="size-px" />
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={loadingMore}
            onClick={onLoadMore}
            testId={`${PREFIX}-load-more`}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </>
  );
}
